// URLSearchParams を使うモジュールと同居させて、利用側での入れ忘れを防ぐ(ネイティブ実装があれば何もしない)
import 'url-search-params-polyfill'

/**
 * ホテラバ専用商品一覧の絞り込み条件モデル。
 *
 * ## 設計: 語彙はひとつ、表現は3つ
 *
 * 正規形 {@link ItemListCondition} を中心に、「/itemlist のURLクエリ」と「BFFへのリクエストクエリ」を
 * 相互変換する。**URLの語彙とBFFの語彙は同一視しており、意味を翻訳する層は作らない**。
 *
 * - `parseItemListCondition`: URL → 正規形。既定値の充填と不正値のフォールバックを行う唯一の入口
 * - `serializeForApi` / `serializeForUrl`: 正規形 → BFFリクエスト / URL。両者の差は
 *   媒体都合の機械的な表現差のみ(page/limitの有無・配列の`key[]`形式/CSV形式・sortの常時明示/既定時省略。
 *   詳細は各関数のコメント参照)
 *
 * 語彙を同一視するのは、新フロントがBFFを直叩きする将来像(front PHPは純proxyの過渡層)において、
 * 翻訳層を作ると移行時にそれごと持ち運ぶことになるため。URLをBFF語彙のままにしておけば、
 * 移行時に捨てるのはPHP proxyだけでURLはそのまま生きる。
 * トレードオフとして、URLがBFFの語彙に結合する(BFF側がパラメータをrenameするとURLの後方互換問題になり、
 * そのとき初めて翻訳を検討する)。
 *
 * 値の集合(enum)はBFFの `/inter-server/fku-items/colored-contact-lens` が受理する契約に合わせている。
 * @see https://github.com/Everforth/pj-hotel-lovers-api/blob/develop/src/inter-server-api/inter-server-api.controller.ts
 *
 * ## なぜ api client 側でなく model にあるか
 *
 * この正規形は**BFFへのリクエストクエリだけを表現するものではない**。URL・画面表示(選択中ラベル/タイトル)・
 * BFFリクエストという複数の表現の中心にある「ページの絞り込み条件」そのものなので、
 * BFF通信の付属物としてapi clientに置かず、modelとして独立させている(api clientはtransportに徹する)。
 *
 * ## 絞り込み条件でないURLクエリ
 *
 * /itemlist のURLには条件のほかに parts(ページパーツ指定)や
 * 計測用等の未知のクエリも載るが、本モジュールはそれらに関心を持たない。
 * ページURL・canonicalへの合成はmodel/page-url.tsの責務で、本モジュールは合成の材料として
 * 条件部分の正規形クエリ(serializeForUrl)と、条件が所有するキー一覧(conditionQueryKeys)を提供する。
 *
 * ## スコープ外
 *
 * - 旧 /itemlist の既存語彙(`category=`/`color_family=` 等)からの翻訳。例外は `keywords`→`q` の読み替えのみ
 *   (parseItemListConditionのコメント参照)。旧URLからのリダイレクト自体はhtaccessの責務
 * - HTTP通信(api client)・画面状態の保持(App)・候補値の取得
 * - ページURL・canonicalの合成(model/page-url.ts。originとpathの組み立てはwindow依存のためApp側)
 */

export type SortType = 'popularity' | 'newArrivals' | 'priceAsc'
// enumフィルタの値に、未指定を意味するBFF語彙(`both`/`all`)は含めない。
// UIに「すべて」の選択肢が無く(再タップで解除)、正規形では未指定をnullで表すため、値として現れる場所が無い
export type BorderFilter = 'on' | 'off'
export type PowerFilter = 'with_power' | 'without_power' | 'with_plus_power'
export type WaterContentFilter = 'low' | 'high'
export type HighPowerFilter = 'minus_8' | 'minus_10'
export type ExpirationPeriod = 'oneDay' | 'twoWeeks' | 'oneMonth'

/**
 * filterのキー一覧(種類別)。この配列群が唯一の情報源で、キーのunion型はtypeofで導出する。
 * ItemListConditionも配列/booleanのフィールドをここから導出(Record extends)しているため、
 * キーを追加すると DEFAULT_CONDITION と parseItemListCondition の更新が型エラーとなる。
 * これによってserialize系の反復からキーが漏れて「選んでも送信されないフィルタ」になる事故を防ぐことができる。
 *
 * 配列filterのうち、値が候補API由来の自由文字列であるものと、値の語彙を本モジュールが
 * 静的に持つ expirationPeriod(ExpirationPeriod[]型)は要素型が異なるため、キー一覧を分けて合成する。
 */
const stringArrayFilterKeys = [
  'baseCurve',
  'colorFamilies',
  'diameter',
  'graphicDiameter',
] as const
export type StringArrayFilterKey = typeof stringArrayFilterKeys[number]
export type ArrayFilterKey = StringArrayFilterKey | 'expirationPeriod'

const arrayFilterKeys: ReadonlyArray<ArrayFilterKey> = [...stringArrayFilterKeys, 'expirationPeriod']

const booleanFilterKeys = [
  'includeTrialProducts',
  'onlyAstigmatism',
  'onlyBundleDiscountsApplied',
  'onlyMoist',
  'onlyNonRotatingSuikou',
  'onlySilicon',
  'onlySuikou',
  'onlyTrialProducts',
  'onlyUvCut',
] as const
export type BooleanFilterKey = typeof booleanFilterKeys[number]

const enumFilterKeys = [
  'hasBorder',
  'highPowerFilter',
  'powerFilter',
  'waterContentFilter',
] as const
export type EnumFilterKey = typeof enumFilterKeys[number]

/**
 * enumフィルタのキーごとの値の型。キーごとに要素型が異なるためRecordでは表せず、
 * このinterfaceを対応表としてItemListConditionのフィールド(mapped typeでnullを合成)と
 * toggleEnumFilterの引数型を導出する(キーの増減はここの過不足がコンパイルエラーになる)。
 */
export interface EnumFilterValues {
  readonly hasBorder: BorderFilter
  readonly highPowerFilter: HighPowerFilter
  readonly powerFilter: PowerFilter
  readonly waterContentFilter: WaterContentFilter
}

// enumフィルタの未指定(その軸で絞らない)はnull。空配列・空文字と同列の「絞り込み無し」の構造表現
type NullableEnumFilters = {
  readonly [K in EnumFilterKey]: EnumFilterValues[K] | null
}

export interface ItemListCondition extends
  Readonly<Record<StringArrayFilterKey, string[]>>,
  Readonly<Record<BooleanFilterKey, boolean>>,
  NullableEnumFilters {
  readonly q: string
  readonly sort: SortType
  readonly expirationPeriod: ExpirationPeriod[]
}

export interface SelectOption<T extends string = string> {
  readonly value: T
  readonly label: string
}

export interface BooleanFilterDefinition {
  readonly key: BooleanFilterKey
  readonly label: string
}

export const PAGE_SIZE = 24

export const sortOptions: ReadonlyArray<SelectOption<SortType>> = [
  { value: 'popularity', label: '人気順' },
  { value: 'newArrivals', label: '新着順' },
  { value: 'priceAsc', label: '安い順' },
]

// この並び(ワンデー→1ヶ月→2週間)が装用期間の唯一の正の順序(値の重要さの順)。
// 絞り込みモーダルの選択肢の表示順も、配列値の正規順序(sortExpirationPeriods)もこの順序に従う
export const expirationPeriodOptions: ReadonlyArray<SelectOption<ExpirationPeriod>> = [
  { value: 'oneDay', label: 'ワンデー' },
  { value: 'oneMonth', label: '1ヶ月' },
  { value: 'twoWeeks', label: '2週間' },
]

/**
 * 配列filterの値の正規順序。正規形は常にこの順序で値を保持する(parse・toggle系が適用する)。
 * 順序を正規形の不変条件にするのは、同じ条件(値の集合)が選択順やURLの記載順によって
 * 別のURL・canonical・title/descriptionにならないようにするため(SEO要件)。
 *
 * - expirationPeriod: 語彙を本モジュールが所有するため、選択肢の定義順(expirationPeriodOptions)を正とする
 * - その他のキー(候補API/behaviorが語彙を所有): 本モジュールにとって値は不透明な識別子で意味上の順序を
 *   定義できないため、辞書順を正規順序と定める(候補の表示順に依存させると、候補取得前のparse時に
 *   正規化できず順序が実行時データに揺れる)
 */
function sortStringArrayValues(values: string[]): string[] {
  return [...values].sort()
}

function sortExpirationPeriods(values: ExpirationPeriod[]): ExpirationPeriod[] {
  const order = expirationPeriodOptions.map(option => option.value)

  return [...values].sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

/**
 * ラベルはiOSアプリの絞り込み画面(ProductListFilterView)と同じ文言。
 * 並びは絞り込みモーダルの表示順(セール→カラーオプション→乱視→オプション)に合わせている。
 * includeTrialProductsはモーダルには置かないが、URL/BFFの語彙としては受け付ける。
 */
export const booleanFilterDefinitions: ReadonlyArray<BooleanFilterDefinition> = [
  { key: 'onlyBundleDiscountsApplied', label: 'まとめて割引対象' },
  { key: 'onlyTrialProducts', label: 'お試しカラコン' },
  { key: 'onlySuikou', label: '水光' },
  { key: 'onlyNonRotatingSuikou', label: '回らない水光' },
  { key: 'onlyAstigmatism', label: '乱視' },
  { key: 'onlyUvCut', label: 'UVカット' },
  { key: 'onlyMoist', label: 'モイスト' },
  { key: 'onlySilicon', label: 'シリコーン' },
  { key: 'includeTrialProducts', label: 'お試しを含む' },
]

export const enumFilterOptions: {
  readonly [K in EnumFilterKey]: ReadonlyArray<SelectOption<EnumFilterValues[K]>>
} = {
  hasBorder: [
    { value: 'on', label: 'フチあり' },
    { value: 'off', label: 'フチなし' },
  ],
  highPowerFilter: [
    { value: 'minus_8', label: '〜-8.00' },
    { value: 'minus_10', label: '〜-10.00' },
  ],
  powerFilter: [
    { value: 'with_power', label: '度あり' },
    { value: 'without_power', label: '度なし' },
    { value: 'with_plus_power', label: '遠視対応あり' },
  ],
  waterContentFilter: [
    { value: 'low', label: '低含水' },
    { value: 'high', label: '高含水' },
  ],
}

/**
 * 既定値=「何も絞り込んでいない」状態。すべてのフィルタを構造的な空(空文字・空配列・false・null)で表す。
 * 「その軸で絞らない」に明示の値(BFF語彙の`both`/`all`)を割り当てないのは、UIに「すべて」の選択肢が無く
 * (再タップで解除。toggleEnumFilterのコメント参照)、送信時も省略する(serializeForApiのコメント参照)ため。
 * 例外はsortのみ: 並び順に「未指定」は無く常に値を持つ。既定はBFF・アプリの既定(popularity)と
 * 意図的に異なり、既存 /itemlist の並び順(新着順)を継承する(このためsortだけは常に明示送信する)。
 */
const DEFAULT_CONDITION: ItemListCondition = {
  q: '',
  sort: 'newArrivals',
  baseCurve: [],
  colorFamilies: [],
  diameter: [],
  expirationPeriod: [],
  graphicDiameter: [],
  includeTrialProducts: false,
  onlyAstigmatism: false,
  onlyBundleDiscountsApplied: false,
  onlyMoist: false,
  onlyNonRotatingSuikou: false,
  onlySilicon: false,
  onlySuikou: false,
  onlyTrialProducts: false,
  onlyUvCut: false,
  hasBorder: null,
  highPowerFilter: null,
  powerFilter: null,
  waterContentFilter: null,
}

export function createDefaultCondition(): ItemListCondition {
  return cloneCondition(DEFAULT_CONDITION)
}

export function cloneCondition(condition: ItemListCondition): ItemListCondition {
  // 配列フィールドを列挙で複製すると、キー追加時に先頭のspreadが型を満たしてしまい
  // 「新しい配列だけ参照共有」の複製漏れがコンパイルエラーにならない。キー一覧からの反復で複製する
  // (要素型の異なる expirationPeriod のみ個別に複製。漏れはspecの生成的テストが検出する)
  const clonedArrays: Partial<Record<StringArrayFilterKey, string[]>> = {}
  stringArrayFilterKeys.forEach((key) => {
    clonedArrays[key] = [...condition[key]]
  })

  return { ...condition, ...clonedArrays, expirationPeriod: [...condition.expirationPeriod] }
}

// --- 条件の操作 ---
// ItemListConditionは読み取り専用の正規形で、変更は常に「元の条件から新しい条件を作って返す」
// 以下の操作関数で行う。キーの種類ごとの更新規則(トグル・既定値へ戻す等)をここに閉じ、
// 利用側(FilterModal / App)が内部構造(キー一覧・要素型の違い)に依存して直接spreadで組み立てないようにする。

export function setKeyword(condition: ItemListCondition, q: string): ItemListCondition {
  return { ...cloneCondition(condition), q }
}

export function setSort(condition: ItemListCondition, sort: SortType): ItemListCondition {
  return { ...cloneCondition(condition), sort }
}

/**
 * 配列filterの値の選択をトグルする(未選択なら加え、選択中なら外す)。
 * 値は集合であり、選択順は保持せず正規順序で持つ(sortStringArrayValuesのコメント参照)。
 */
export function toggleArrayValue(
  condition: ItemListCondition,
  key: StringArrayFilterKey,
  value: string
): ItemListCondition {
  const current = condition[key]
  const next = current.indexOf(value) >= 0
    ? current.filter(item => item !== value)
    : sortStringArrayValues([...current, value])

  return { ...cloneCondition(condition), [key]: next }
}

// 要素型がExpirationPeriodで他の配列filter(候補API由来の自由文字列)と異なるため個別に扱う(キー一覧のコメント参照)
export function toggleExpirationPeriod(condition: ItemListCondition, value: ExpirationPeriod): ItemListCondition {
  const current = condition.expirationPeriod
  const next = current.indexOf(value) >= 0
    ? current.filter(item => item !== value)
    : sortExpirationPeriods([...current, value])

  return { ...cloneCondition(condition), expirationPeriod: next }
}

export function toggleBooleanFilter(condition: ItemListCondition, key: BooleanFilterKey): ItemListCondition {
  return { ...cloneCondition(condition), [key]: !condition[key] }
}

/**
 * enumフィルタの値を選択する。選択中の値を再指定したときは解除(未指定=nullへ戻す)。
 * 「すべて」の選択肢を置かず再タップで解除する絞り込みUIの規則を、UI側でなくここで持つ。
 * valueはキーに対応するenum型(EnumFilterValues[K])に限定し、別キーの値の混入をコンパイルエラーにする。
 */
export function toggleEnumFilter<K extends EnumFilterKey>(
  condition: ItemListCondition,
  key: K,
  value: EnumFilterValues[K]
): ItemListCondition {
  const next = condition[key] === value ? null : value

  return { ...cloneCondition(condition), [key]: next }
}

/**
 * 絞り込み条件をすべて既定値へ戻す。並び順は絞り込み条件ではないので維持する。
 */
export function resetFilters(condition: ItemListCondition): ItemListCondition {
  return { ...createDefaultCondition(), sort: condition.sort }
}

/**
 * 選択中の条件1つ分(SelectedFilter)だけを外した(既定値へ戻した)条件を返す。
 */
export function removeSelectedFilter(condition: ItemListCondition, filter: SelectedFilter): ItemListCondition {
  switch (filter.kind) {
    case 'q':
      return setKeyword(condition, '')
    case 'array': {
      // 要素型の異なるexpirationPeriod(ExpirationPeriod[])だけ個別に外す(キー一覧のコメント参照)
      const key = filter.key
      if (key === 'expirationPeriod') {
        return {
          ...cloneCondition(condition),
          expirationPeriod: condition.expirationPeriod.filter(value => value !== filter.value),
        }
      }

      return {
        ...cloneCondition(condition),
        [key]: condition[key].filter(value => value !== filter.value),
      }
    }
    case 'boolean':
      return { ...cloneCondition(condition), [filter.key]: false }
    case 'enum':
      return { ...cloneCondition(condition), [filter.key]: null }
  }
}

/**
 * location.search からクエリを解釈する。
 * 不正な値(未知のenum値など)は未指定へフォールバックし、未知のパラメータは無視する。
 * 未指定を意味するBFF語彙(`both`/`all`)も未知の値として扱う(どちらも未指定へ収束するため区別しない)。
 *
 * 例外として、旧 /itemlist/search の検索語キー `keywords` は `q` として解釈する(qの明示があればqを優先)。
 * 旧ページの語彙は解釈しない方針だが、keywordsだけは顧客が外部ツールから
 * /itemlist/search?keywords=… へ直接リンクしている実績があり、リダイレクト後も検索意図を引き継ぐ。
 * 配列は `?diameter=a&diameter=b` の繰り返し形式と `?diameter=a,b` のCSV形式の両方を受理する。
 * 絞り込み条件は集合(UIの選択状態と対応)なので、重複する値は1つにまとめ、
 * 値の順序も正規順序へ正規化する(sortStringArrayValues / sortExpirationPeriodsのコメント参照)。
 * 配列フィルタの「値」は原則検証せず素通しする。語彙(`D14_0` 等)はBFFと候補APIが所有しており
 * 本モジュールでは正誤を静的に判定できないため、検証は候補値を持つ呼び出し側とBFFに委ねる。
 * 例外は expirationPeriod のみ: 語彙を本モジュールが静的に持つ(expirationPeriodOptions)ため、
 * 未知の値は他のenumフィルタと同様ここで除外する。
 */
export function parseItemListCondition(search: string): ItemListCondition {
  const params = new URLSearchParams(search)

  return {
    q: normalizeString(params.get('q')) || normalizeString(params.get('keywords')),
    sort: normalizeEnum(params.get('sort'), sortOptions, DEFAULT_CONDITION.sort),
    baseCurve: sortStringArrayValues(normalizeArray(params.getAll('baseCurve'))),
    colorFamilies: sortStringArrayValues(normalizeArray(params.getAll('colorFamilies'))),
    diameter: sortStringArrayValues(normalizeArray(params.getAll('diameter'))),
    expirationPeriod: sortExpirationPeriods(
      normalizeArray(params.getAll('expirationPeriod'))
        .filter((value): value is ExpirationPeriod => expirationPeriodOptions.some(option => option.value === value))
    ),
    graphicDiameter: sortStringArrayValues(normalizeArray(params.getAll('graphicDiameter'))),
    includeTrialProducts: normalizeBoolean(params.get('includeTrialProducts')),
    onlyAstigmatism: normalizeBoolean(params.get('onlyAstigmatism')),
    onlyBundleDiscountsApplied: normalizeBoolean(params.get('onlyBundleDiscountsApplied')),
    onlyMoist: normalizeBoolean(params.get('onlyMoist')),
    onlyNonRotatingSuikou: normalizeBoolean(params.get('onlyNonRotatingSuikou')),
    onlySilicon: normalizeBoolean(params.get('onlySilicon')),
    onlySuikou: normalizeBoolean(params.get('onlySuikou')),
    onlyTrialProducts: normalizeBoolean(params.get('onlyTrialProducts')),
    onlyUvCut: normalizeBoolean(params.get('onlyUvCut')),
    hasBorder: normalizeOptionalEnum(params.get('hasBorder'), enumFilterOptions.hasBorder),
    highPowerFilter: normalizeOptionalEnum(params.get('highPowerFilter'), enumFilterOptions.highPowerFilter),
    powerFilter: normalizeOptionalEnum(params.get('powerFilter'), enumFilterOptions.powerFilter),
    waterContentFilter: normalizeOptionalEnum(params.get('waterContentFilter'), enumFilterOptions.waterContentFilter),
  }
}

/**
 * hl-proxy(=BFF素通し)へ送るクエリパラメータへ変換する。
 * 未指定(空のq・空配列・falseのboolean・nullのenum)は「その軸で絞らない」の構造表現なので送らない。
 * 省略時の解釈はBFF側のDTO既定値(絞らない)が担う
 * (@see https://github.com/Everforth/pj-hotel-lovers-api/blob/develop/src/products/dto/colored-contact-lens-filter.query.dto.ts)。
 * 例外としてsortだけは常に明示送信する。hl-itemlistの既定(newArrivals)がBFFの既定(popularity)と
 * 意図的に異なるため、省略すると並び順が変わってしまう(DEFAULT_CONDITIONのコメント参照)。
 * 配列は `key[]` 形式にする(PHPが配列として受理し、そのままBFFへ中継される)。
 *
 * キーはアルファベット順、配列の値は正規形の正規順序のまま出力する。
 * この戻り値の挿入順がapi clientの組むリクエスト文字列の順序=CDNキャッシュキーになる。
 * 「同一の条件(値の選択順は問わない)は常に同一のリクエスト文字列」の保証は、
 * 正規形が値の順序の一意性を持つこと(sortStringArrayValues / sortExpirationPeriodsのコメント参照)に乗る。
 */
export function serializeForApi(
  condition: ItemListCondition,
  page?: number
): { [key: string]: string | string[] | number } {
  const params: { [key: string]: string | string[] | number } = {}

  if (page !== undefined) {
    params.page = page
    params.limit = PAGE_SIZE
  }
  if (condition.q !== '') {
    params.q = condition.q
  }
  params.sort = condition.sort

  arrayFilterKeys.forEach((key) => {
    if (condition[key].length > 0) {
      params[`${key}[]`] = [...condition[key]]
    }
  })
  booleanFilterKeys.forEach((key) => {
    if (condition[key]) {
      params[key] = 'true'
    }
  })
  enumFilterKeys.forEach((key) => {
    const value = condition[key]
    if (value !== null) {
      params[key] = value
    }
  })

  const sorted: { [key: string]: string | string[] | number } = {}
  Object.keys(params).sort().forEach((key) => {
    sorted[key] = params[key]
  })

  return sorted
}

/**
 * 条件部分の正規形クエリ文字列へ変換する。未指定のキーは省略し、キーはアルファベット順で出力する
 * (同じ条件なら常に同じ文字列になる)。ページURL・canonicalへの合成はmodel/page-url.tsが行う。
 * 配列はCSV形式(`diameter=a,b`)にする。繰り返し形式(`diameter=a&diameter=b`)にしないのは、
 * server側のcanonical組み立て(CanonicalUrlのRemoveQueriesが使うparse_str)が繰り返しキーを
 * 最後の値しか残せず、canonicalから条件が欠落するため(parseは両形式を受理する)。
 * 配列の値は正規形が保持する正規順序(sortStringArrayValues / sortExpirationPeriodsのコメント参照)のまま出力する。
 */
export function serializeForUrl(condition: ItemListCondition): string {
  const valuesByKey: { [key: string]: string[] } = {}
  const add = (key: string, value: string) => {
    if (!valuesByKey[key]) {
      valuesByKey[key] = []
    }
    valuesByKey[key].push(value)
  }

  if (condition.q !== '') {
    add('q', condition.q)
  }
  if (condition.sort !== DEFAULT_CONDITION.sort) {
    add('sort', condition.sort)
  }
  arrayFilterKeys.forEach((key) => {
    if (condition[key].length > 0) {
      add(key, condition[key].join(','))
    }
  })
  booleanFilterKeys.forEach((key) => {
    if (condition[key]) {
      add(key, 'true')
    }
  })
  enumFilterKeys.forEach((key) => {
    const value = condition[key]
    if (value !== null) {
      add(key, value)
    }
  })

  const params = new URLSearchParams()
  Object.keys(valuesByKey).sort().forEach((key) => {
    valuesByKey[key].forEach(value => params.append(key, value))
  })

  return params.toString()
}

// URLクエリのうち条件が所有するキー。条件の変更でURLを組み立て直すとき、
// 上書きしてよいのはこのキーだけ(合成の規則はmodel/page-url.tsのコメント参照)
export const conditionQueryKeys: ReadonlyArray<string> = [
  'q',
  // 旧 /itemlist/search の検索語(parseがqへ読み替える)。所有キーにしないとURLに残り続け、
  // キーワードを解除してもリロードや戻るで検索語が復活してしまう。書き換え後はqだけが正規形として残る
  'keywords',
  'sort',
  ...arrayFilterKeys,
  ...booleanFilterKeys,
  ...enumFilterKeys,
]

/**
 * 選択中の絞り込み条件1つ分の識別子。「この条件だけ解除する」操作(removeSelectedFilter)の単位で、
 * 表示情報(ラベル)は含まない。
 */
export type SelectedFilter =
  { readonly kind: 'q' } |
  { readonly kind: 'array', readonly key: ArrayFilterKey, readonly value: string } |
  { readonly kind: 'boolean', readonly key: BooleanFilterKey } |
  { readonly kind: 'enum', readonly key: EnumFilterKey }

/**
 * 選択中条件のチップ(解除操作つきの表示単位)。filterが解除対象の識別子、labelが表示文字列。
 */
export interface ActiveFilterChip {
  readonly filter: SelectedFilter
  readonly label: string
}

/**
 * 配列filterのキーの表示見出し。値のラベル(「14.0mm」等)だけでは何の数値か判別できないキーに付ける。
 * チップでは値ごとに「DIA 14.0mm」、title/descriptionではキーごとに「DIA 14.0mm, 14.2mm」と
 * 1回だけ付ける(arrayFilterGroupLabelのコメント参照)ため、値のラベルには含めず本モジュールが合成する。
 */
const arrayFilterLabelPrefixes: Readonly<Record<ArrayFilterKey, string>> = {
  baseCurve: 'BC',
  colorFamilies: '',
  diameter: 'DIA',
  expirationPeriod: '',
  graphicDiameter: '着色',
}

function withArrayLabelPrefix(key: ArrayFilterKey, body: string): string {
  const prefix = arrayFilterLabelPrefixes[key]

  return prefix === '' ? body : `${prefix} ${body}`
}

/**
 * 配列filterの値→表示ラベル(値部分のみ。キーの見出しはarrayFilterLabelPrefixes)の解決。
 * 候補APIに依存するキーは注入されたresolverに委ね、
 * 語彙を本モジュールが持つexpirationPeriodはここで解決する(チップとタイトル/説明文で共用)。
 */
function resolveArrayValueLabel(
  key: ArrayFilterKey,
  value: string,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): string {
  if (key === 'expirationPeriod') {
    const option = expirationPeriodOptions.find(item => item.value === value)

    return option ? option.label : value
  }

  return arrayOptionLabel(key, value)
}

/**
 * 配列filterの選択値を「見出し(あれば) + 値のカンマ区切り」の1ラベルにまとめる(title/description用)。
 * 値ごとに見出しを付けると「着色 11.2mm 着色 11.9mm」のように見出しが繰り返されて冗長なため、
 * 「着色 11.2mm, 11.9mm」の形にする(1値=1チップで解除単位を示すチップとは分けている)。
 * 未選択のキーは空文字列を返す。
 */
function arrayFilterGroupLabel(
  condition: ItemListCondition,
  key: ArrayFilterKey,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): string {
  const values: ReadonlyArray<string> = condition[key]
  if (values.length === 0) {
    return ''
  }

  return withArrayLabelPrefix(
    key,
    values.map(value => resolveArrayValueLabel(key, value, arrayOptionLabel)).join(', ')
  )
}

/**
 * 選択中の条件をチップ(解除操作つきの表示単位)として列挙する。
 * チップという独立した状態があるわけではなく、serializeForUrl等と同列の「正規形からの導出表現」のひとつ。
 * 配列filterは値ごとに1チップになる
 * (選択単位は常に1値。カラーの選択肢が1値=1ラベルである根拠はcolor-family.tsのコメント参照)。
 * 値→ラベル変換は候補APIのレスポンスに依存するため呼び出し側から注入する(resolveArrayValueLabel参照)。
 */
export function activeFilterChips(
  condition: ItemListCondition,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []

  if (condition.q !== '') {
    chips.push({ filter: { kind: 'q' }, label: `キーワード: ${condition.q}` })
  }
  arrayFilterKeys.forEach((key) => {
    condition[key].forEach((value: string) => {
      chips.push({
        filter: { kind: 'array', key, value },
        label: withArrayLabelPrefix(key, resolveArrayValueLabel(key, value, arrayOptionLabel)),
      })
    })
  })
  booleanFilterDefinitions.forEach((definition) => {
    if (condition[definition.key]) {
      chips.push({ filter: { kind: 'boolean', key: definition.key }, label: definition.label })
    }
  })
  enumFilterKeys.forEach((key) => {
    const value = condition[key]
    if (value !== null) {
      const options: ReadonlyArray<SelectOption> = enumFilterOptions[key]
      const option = options.find(item => item.value === value)
      chips.push({ filter: { kind: 'enum', key }, label: option ? option.label : value })
    }
  })

  return chips
}

/**
 * タイトルに載せる「主要な絞り込み条件」。
 * 絞り込み条件には強弱があり、すべてをタイトルに並べると主要な条件が埋もれるため、
 * ここに列挙した条件だけを、この順序(装用期間→カラー→DIA→着色直径→オプション)で載せる。
 */
const majorArrayFilterKeysForTitle: ReadonlyArray<ArrayFilterKey> = [
  'expirationPeriod',
  'colorFamilies',
  'diameter',
  'graphicDiameter',
]
const majorBooleanFilterKeysForTitle: ReadonlyArray<BooleanFilterKey> = [
  'onlyNonRotatingSuikou',
  'onlySuikou',
  'onlySilicon',
]

function majorFilterLabelsForTitle(
  condition: ItemListCondition,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): string[] {
  const labels: string[] = []

  majorArrayFilterKeysForTitle.forEach((key) => {
    const group = arrayFilterGroupLabel(condition, key, arrayOptionLabel)
    if (group !== '') {
      labels.push(group)
    }
  })
  majorBooleanFilterKeysForTitle.forEach((key) => {
    if (condition[key]) {
      const definition = booleanFilterDefinitions.find(item => item.key === key)
      labels.push(definition ? definition.label : key)
    }
  })

  return labels.filter((label, index) => labels.indexOf(label) === index)
}

/**
 * 絞り込み条件からページタイトル(ページ名部分)を組み立てる。
 *
 * - キーワードのみ: 「Foo の検索結果」
 * - 主要な絞り込みのみ: 「ワンデー ブラウン のカラコンを探す」(主要条件をスペース区切り。
 *   同一キーの複数値は「着色 11.2mm, 11.9mm」の1まとまり。arrayFilterGroupLabelのコメント参照)
 * - 両方: 「Foo の検索結果 [ワンデー ブラウン]」
 * - どちらも無し: 「カラコンを探す」— サーバー(templates/Hl/Itemlist/index.php)が
 *   初期表示用にassignするtitleと一致させること
 *
 * 「 | サイトタイトル」の部分はここでは組み立てず、サーバー(MetadataHelper)が
 * 出力した初期タイトルから呼び出し側(App)が引き継ぐ(他のMPAページと同じ供給源にするため)。
 */
export function titleForCondition(
  condition: ItemListCondition,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): string {
  const majorLabels = majorFilterLabelsForTitle(condition, arrayOptionLabel)

  if (condition.q !== '') {
    return majorLabels.length > 0
      ? `${condition.q} の検索結果 [${majorLabels.join(' ')}]`
      : `${condition.q} の検索結果`
  }

  return majorLabels.length > 0 ? `${majorLabels.join(' ')} のカラコンを探す` : 'カラコンを探す'
}

/**
 * タイトルに載せない非主要な絞り込み条件(descriptionにだけ載せる)。
 * 順序は BC→こだわり系(チップと同じ並び)→フチ→度数→高度数→含水率。
 */
function minorFilterLabelsForDescription(
  condition: ItemListCondition,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): string[] {
  const labels: string[] = []

  const baseCurveGroup = arrayFilterGroupLabel(condition, 'baseCurve', arrayOptionLabel)
  if (baseCurveGroup !== '') {
    labels.push(baseCurveGroup)
  }
  booleanFilterDefinitions.forEach((definition) => {
    if (majorBooleanFilterKeysForTitle.indexOf(definition.key) >= 0) {
      return
    }
    if (condition[definition.key]) {
      labels.push(definition.label)
    }
  })

  const enumOrder: ReadonlyArray<EnumFilterKey> = ['hasBorder', 'powerFilter', 'highPowerFilter', 'waterContentFilter']
  enumOrder.forEach((key) => {
    const value = condition[key]
    if (value !== null) {
      const options: ReadonlyArray<SelectOption> = enumFilterOptions[key]
      const option = options.find(item => item.value === value)
      labels.push(option ? option.label : value)
    }
  })

  return labels.filter((label, index) => labels.indexOf(label) === index)
}

/**
 * 絞り込み条件から、meta descriptionの先頭に足す条件文を組み立てる(条件が無ければ空文字列=既定文のまま)。
 * 呼び出し側(App)は、この条件文に続けてサーバーが出力した既定のdescriptionをつなげる。
 *
 * titleは主要な条件しか載せないため、titleだけでは非主要な条件だけが異なるURL同士を区別できない。
 * descriptionには非主要な条件まで載せることで、canonicalが異なるURLをdescriptionでも区別する(SEO要件)。
 * 表示はPCで全角90〜120字・スマホで60〜70字程度で切り詰められるため、重要な条件から前詰めにする。
 */
export function descriptionForCondition(
  condition: ItemListCondition,
  arrayOptionLabel: (key: StringArrayFilterKey, value: string) => string
): string {
  const majorLabels = majorFilterLabelsForTitle(condition, arrayOptionLabel)
  const minorLabels = minorFilterLabelsForDescription(condition, arrayOptionLabel)

  if (condition.q !== '') {
    const filterClause = [majorLabels.join(' '), minorLabels.join('・')]
      .filter(part => part !== '')
      .join('、')

    return filterClause !== ''
      ? `「${condition.q}」の検索結果。${filterClause}で絞り込み。`
      : `「${condition.q}」の検索結果。`
  }

  if (majorLabels.length > 0) {
    return minorLabels.length > 0
      ? `${majorLabels.join(' ')} のカラコン一覧。${minorLabels.join('・')}で絞り込み。`
      : `${majorLabels.join(' ')} のカラコン一覧。`
  }

  if (minorLabels.length > 0) {
    return `カラコン一覧。${minorLabels.join('・')}で絞り込み。`
  }

  return ''
}

function normalizeString(value: string | null): string {
  return (value || '').trim()
}

function normalizeArray(values: string[]): string[] {
  return values
    .reduce<string[]>((acc, value) => [...acc, ...value.split(',')], [])
    .map(value => value.trim())
    .filter(value => value !== '')
    .filter((value, index, normalized) => normalized.indexOf(value) === index)
}

function normalizeBoolean(value: string | null): boolean {
  const normalized = normalizeString(value).toLowerCase()

  return normalized === 'true' || normalized === '1' || normalized === 'on'
}

// enumフィルタ用。未知の値・未指定はnull(=その軸で絞らない)へ倒す
function normalizeOptionalEnum<T extends string>(
  value: string | null,
  options: ReadonlyArray<SelectOption<T>>
): T | null {
  const normalized = normalizeString(value)
  const matched = options.find(option => option.value === normalized)

  return matched ? matched.value : null
}

// sort用。並び順に「未指定」は無いため、未知の値は既定値へ倒す
function normalizeEnum<T extends string>(
  value: string | null,
  options: ReadonlyArray<SelectOption<T>>,
  fallback: T
): T {
  const matched = normalizeOptionalEnum(value, options)

  return matched !== null ? matched : fallback
}
